import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Platform-agnostic HTTP client interface
 * Can be implemented differently for web (browser) and React Native
 */
export interface HttpClient {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}

/**
 * Token provider interface
 * Platform-specific implementations provide tokens
 */
export interface TokenProvider {
  getAccessToken: () => Promise<string | null>;
  getXsrfToken?: () => string | null; // Optional, only for web
}

/**
 * Configuration for creating an API client
 */
export interface ApiClientConfig {
  baseURL: string;  // Should be the full regionBaseUrl (e.g., 'https://region.example.com' or '' for same-origin)
  tokenProvider: TokenProvider;
  onUnauthorized?: () => void;
  onForbidden?: () => void;
  withCredentials?: boolean; // For web browsers with cookies
  /**
   * Sent as Accept-Language on every request. For platforms with no browser header (React Native),
   * so the region can pick an email language for a user who has not chosen one.
   */
  acceptLanguage?: string;
}

/**
 * Creates a configured Axios instance for API calls
 * This is platform-agnostic - works for both web and React Native
 * 
 * Note: For Region APIs, the baseURL should be the regionBaseUrl from config.
 * All API endpoints will be called as: {baseURL}/api/{endpoint}
 * Example: 'https://region.example.com/api/boards'
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const { baseURL, tokenProvider, onUnauthorized, onForbidden, withCredentials = false, acceptLanguage } = config;

  const client: AxiosInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      ...(acceptLanguage ? { 'Accept-Language': acceptLanguage } : {}),
    },
    withCredentials,
  });

  // Request interceptor - add auth token
  client.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig) => {
      // Add Bearer token
      const token = await tokenProvider.getAccessToken();
      if (token && requestConfig.headers) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }

      // Add XSRF token (web only)
      if (tokenProvider.getXsrfToken && requestConfig.headers) {
        const xsrf = tokenProvider.getXsrfToken();
        if (xsrf) {
          requestConfig.headers['X-XSRF-TOKEN'] = xsrf;
        }
      }

      return requestConfig;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[API] Unauthorized request');
        onUnauthorized?.();
      }

      if (error.response?.status === 403) {
        console.error('[API] Forbidden request');
        onForbidden?.();
      }

      if (error.response?.status >= 500) {
        console.error('[API] Server error:', error.response.status, error.response.data);
      }

      return Promise.reject(error);
    }
  );

  return client;
}
